import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import {
  RiAwardLine,
  RiArrowRightLine,
  RiArrowUpLine,
  RiDownloadLine,
  RiBuildingLine,
  RiMapPinLine,
  RiWifiLine,
  RiFilePdfLine,
} from "react-icons/ri";
import { useSummit } from "../pages/summitdetails";

// ─── THEME COLORS ────────────────────────────────────────────────────────────
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  success: "#10B981",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    section { padding: 48px 24px !important; }
  }
`;

const Globalsponser = () => {
  const { eventId } = useSummit();
  const [downloading, setDownloading] = useState(false);
  const [showBtt, setShowBtt] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);
  const [brochurePdfUrl, setBrochurePdfUrl] = useState("");
  const [brochureData, setBrochureData] = useState({
    title: "Sponsorship Opportunities",
    subtitle: "",
    description: "Join the world's leading innovators and decision-makers at the most influential technology summit of the year.",
    buttonText: "Download Brochure",
    brochureHighlights: [
      "5,000+ Attendees from 50+ Countries",
      "100+ Expert Speakers",
      "50+ Exhibition Booths",
      "Global Media Coverage",
      "Exclusive Networking Events",
      "Brand Visibility Opportunities"
    ],
    isActive: true
  });

  // ── Venue state ──
  const [venue, setVenue] = useState(null);
  const [loadingVenue, setLoadingVenue] = useState(true);

  useEffect(() => {
    if (eventId) {
      fetchSponsors();
      fetchBrochureInfo();
      fetchVenue();
    } else {
      setLoadingSponsors(false);
      setLoadingVenue(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // ── Fetch sponsors ──
  const fetchSponsors = async () => {
    if (!eventId) {
      console.warn("No eventId provided");
      setLoadingSponsors(false);
      return;
    }
    try {
      setLoadingSponsors(true);
      const url = `/api/sponsors/event/${eventId}`;
      const { data } = await axios.get(url);
      if (data.success) {
        setSponsors(data.sponsors || []);
      } else {
        setSponsors([]);
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      setSponsors([]);
    } finally {
      setLoadingSponsors(false);
    }
  };

  // ── Fetch brochure info ──
  const fetchBrochureInfo = async () => {
    if (!eventId) return;
    try {
      const url = `/api/brochure/info/${eventId}`;
      const { data } = await axios.get(url);
      if (data.success) {
        setBrochureData({
          title: data.title || "Sponsorship Opportunities",
          subtitle: data.subtitle || "",
          description: data.description || "Join the world's leading innovators and decision-makers.",
          buttonText: data.buttonText || "Download Brochure",
          brochureHighlights: data.brochureHighlights || [
            "5,000+ Attendees from 50+ Countries",
            "100+ Expert Speakers",
            "50+ Exhibition Booths",
            "Global Media Coverage",
            "Exclusive Networking Events",
            "Brand Visibility Opportunities"
          ],
          isActive: data.isActive !== false
        });
        setBrochurePdfUrl(data.pdfUrl || "");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No brochure info found – using defaults");
        setBrochurePdfUrl("");
      } else {
        console.error("Error fetching brochure data:", error);
      }
    }
  };

  // ── ✅ Fetch active venue ──
  const fetchVenue = async () => {
    if (!eventId) {
      setLoadingVenue(false);
      return;
    }
    try {
      setLoadingVenue(true);
      const url = `/api/venue/active/${eventId}`;
      const { data } = await axios.get(url);
      if (data.success && data.venue) {
        setVenue(data.venue);
      } else {
        setVenue(null);
      }
    } catch (error) {
      console.error("Error fetching venue:", error);
      setVenue(null);
    } finally {
      setLoadingVenue(false);
    }
  };

  // ── Download brochure ──
  const handleDownloadBrochure = async () => {
    if (downloading || !eventId || !brochurePdfUrl) {
      if (!eventId) toast.error("No event specified");
      if (!brochurePdfUrl) toast.error("Brochure not available for download");
      return;
    }

    setDownloading(true);
    toast.loading("Preparing brochure...", { id: "download" });

    try {
      const url = `/api/brochure/download/${eventId}`;
      const response = await axios.get(url, {
        responseType: "blob",
        timeout: 30000,
      });

      if (!response.data || response.data.size === 0) {
        throw new Error("Empty response received");
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `Sponsorship-Brochure.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Brochure downloaded successfully!", { id: "download" });
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download brochure. Please try again later.", { id: "download" });
    } finally {
      setDownloading(false);
    }
  };

  // ── Group sponsors by type ──
  const groupedSponsors = sponsors.reduce((acc, sponsor) => {
    const type = sponsor.sponsorType || 'partner';
    if (!acc[type]) acc[type] = [];
    acc[type].push(sponsor);
    return acc;
  }, {});

  const sponsorTypeOrder = ['platinum', 'gold', 'silver', 'bronze', 'partner'];
  const getSponsorTypeColor = (type) => {
    const colors = {
      platinum: "#E5E4E2",
      gold: "#FFD700",
      silver: "#C0C0C0",
      bronze: "#CD7F32",
      partner: "#1A56DB"
    };
    return colors[type] || "#6B8090";
  };
  const getSponsorTypeLabel = (type) => {
    const labels = {
      platinum: "Platinum Sponsor",
      gold: "Gold Sponsor",
      silver: "Silver Sponsor",
      bronze: "Bronze Sponsor",
      partner: "Partner"
    };
    return labels[type] || type;
  };

  // ── Get first WiFi amenity or fallback ──
  const getWifiText = () => {
    if (venue && venue.amenities && venue.amenities.length > 0) {
      const wifi = venue.amenities.find(a =>
        a.name.toLowerCase().includes('wifi') ||
        a.name.toLowerCase().includes('internet')
      );
      return wifi ? wifi.name : venue.amenities[0]?.name || "Free WiFi";
    }
    return "Free WiFi";
  };

  if (loadingSponsors) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <div style={{ width: 48, height: 48, border: "3px solid #E2F2F0", borderTopColor: "#2A9D8F", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      <Toaster position="top-right" toastOptions={{
        style: { background: T.white, color: T.text, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 13 },
      }} />

      <div style={{ minHeight: "100vh", background: T.offWhite }}>
        
        {/* ─── HERO SECTION ─── */}
        <section style={{ padding: "clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)", background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: "relative", overflow: "hidden" }}>
          <div style={{ maxWidth: 1320, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <span style={{ padding: "5px 14px", borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                <RiAwardLine size={13} style={{ display: "inline", marginRight: 6 }} />
                Sponsors & Partners
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.4rem)", fontWeight: 900, color: T.navy, letterSpacing: "-0.03em", lineHeight: 1.06, marginBottom: 14 }}>
              Our Valued <em style={{ color: T.accentGold, fontStyle: "normal" }}>Sponsors</em>
            </h1>
            <p style={{ fontSize: "clamp(0.88rem, 1.3vw, 1rem)", color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
              Thank you to our generous sponsors who make this summit possible.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* ─── VENUE BADGES – DYNAMIC ─── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px clamp(20px,4vw,64px) 0', display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          {!loadingVenue && venue ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiBuildingLine style={{ color: T.primary, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>{venue.name || "Venue Name"}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiMapPinLine style={{ color: T.accentGold, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>
                  {venue.city && venue.state ? `${venue.city}, ${venue.state}` : venue.city || venue.state || "Location"}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiWifiLine style={{ color: T.primary, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>{getWifiText()}</span>
              </div>
            </>
          ) : (
            // Fallback badges if venue not loaded yet or missing
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiBuildingLine style={{ color: T.primary, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>Manjeera Trinity Corporate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiMapPinLine style={{ color: T.accentGold, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>Hyderabad, Telangana</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.white, padding: '8px 20px', borderRadius: 100, boxShadow: T.shadow }}>
                <RiWifiLine style={{ color: T.primary, fontSize: 16 }} />
                <span style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>Free WiFi</span>
              </div>
            </>
          )}
        </div>

        {/* ─── STATS BAR ─── */}
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "24px 40px 0" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
            <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>500+</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Attendees</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>50+</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Countries</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: "16px 24px", border: `1px solid ${T.border}`, flex: "1 1 auto", minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentTealLight }}>100+</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Speakers</div>
            </div>
          </div>
        </div>

        {/* ─── MAIN CONTENT ─── */}
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 40px 80px" }}>
          
          {/* ── BROCHURE SECTION ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              marginBottom: 60,
              background: T.white,
              borderRadius: 24,
              padding: "48px 48px",
              border: `1px solid ${T.border}`,
              position: "relative",
              overflow: "hidden",
              boxShadow: T.shadow,
            }}
          >
            <div style={{ position: "absolute", top: -100, right: -100, width: 300, height: 300, background: `radial-gradient(circle, ${T.primary}08 0%, transparent 70%)`, borderRadius: "50%" }} />
            <div style={{ position: "absolute", bottom: -100, left: -100, width: 250, height: 250, background: `radial-gradient(circle, ${T.primaryLight}05 0%, transparent 70%)`, borderRadius: "50%" }} />

            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 24, background: `${T.primary}10`, padding: "16px", borderRadius: "50%", border: `1px solid ${T.primary}20` }}>
              <div style={{ width: 64, height: 64, background: T.gradPrimary, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>📄</div>
            </div>

            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, color: T.navy, marginBottom: 12 }}>
              {brochureData.title}
            </h2>

            <p style={{ color: T.primary, fontSize: 18, fontWeight: 600, marginBottom: 24, letterSpacing: "0.02em" }}>
              {brochureData.subtitle}
            </p>

            <p style={{ color: T.textMuted, fontSize: 16, maxWidth: 700, margin: "0 auto 32px", lineHeight: 1.8 }}>
              {brochureData.description}
            </p>

            {/* Highlights */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, maxWidth: 700, margin: "0 auto 40px", textAlign: "left" }}>
              {brochureData.brochureHighlights.map((highlight, index) => (
                <div key={index} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: `${T.primary}04`, borderRadius: 12, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 20 }}>✨</span>
                  <span style={{ color: T.textMuted, fontSize: 14, fontWeight: 500 }}>{highlight}</span>
                </div>
              ))}
            </div>

            {/* Download Button */}
            {brochurePdfUrl ? (
              <div style={{ textAlign: "center" }}>
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDownloadBrochure}
                  disabled={downloading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "14px 36px",
                    background: T.gradGold,
                    color: T.navyDeep,
                    fontWeight: 700,
                    borderRadius: 50,
                    border: "none",
                    fontSize: 15,
                    cursor: downloading ? "not-allowed" : "pointer",
                    opacity: downloading ? 0.7 : 1,
                    fontFamily: "'Inter', sans-serif",
                    boxShadow: T.shadowGold,
                  }}
                >
                  {downloading ? (
                    <>
                      <div style={{ width: 18, height: 18, border: `2px solid ${T.navyDeep}30`, borderTopColor: T.navyDeep, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <RiDownloadLine size={18} />
                      {brochureData.buttonText}
                      <RiArrowRightLine size={16} />
                    </>
                  )}
                </motion.button>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "12px 24px", background: T.offWhite, borderRadius: 12, border: `1px dashed ${T.border}` }}>
                  <RiFilePdfLine size={24} style={{ color: T.textMuted }} />
                  <span style={{ color: T.textMuted, fontSize: 14 }}>Brochure coming soon</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* ─── SPONSORS LIST ─── */}
          {sponsors.length > 0 ? (
            sponsorTypeOrder.map(type => {
              const typeSponsors = groupedSponsors[type] || [];
              if (typeSponsors.length === 0) return null;

              return (
                <motion.div
                  key={type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{ marginBottom: 60 }}
                >
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: T.navy, marginBottom: 32, borderLeft: `4px solid ${getSponsorTypeColor(type)}`, paddingLeft: 20 }}>
                    {getSponsorTypeLabel(type)}
                  </h2>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${type === 'platinum' ? 280 : type === 'gold' ? 240 : 200}px, 1fr))`, gap: type === 'platinum' ? 30 : type === 'gold' ? 25 : 20 }}>
                    {typeSponsors.map((sponsor, i) => (
                      <motion.div
                        key={sponsor._id || i}
                        whileHover={{ y: -5 }}
                        style={{
                          background: T.white,
                          borderRadius: type === 'platinum' ? 20 : type === 'gold' ? 16 : 12,
                          padding: type === 'platinum' ? "30px" : type === 'gold' ? "25px" : "20px",
                          textAlign: "center",
                          boxShadow: T.shadow,
                          border: `1px solid ${T.border}`,
                          transition: "all 0.3s ease",
                        }}
                      >
                        {sponsor.logoUrl ? (
                          <img src={sponsor.logoUrl} alt={sponsor.name} style={{ maxWidth: "100%", maxHeight: type === 'platinum' ? 80 : type === 'gold' ? 60 : 50, objectFit: "contain", marginBottom: type === 'platinum' ? 20 : 15, margin: "0 auto" }} />
                        ) : (
                          <div style={{ width: type === 'platinum' ? 80 : type === 'gold' ? 60 : 50, height: type === 'platinum' ? 80 : type === 'gold' ? 60 : 50, background: T.gradPrimary, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: type === 'platinum' ? 40 : type === 'gold' ? 30 : 25 }}>
                            🏢
                          </div>
                        )}
                        <h3 style={{ fontSize: type === 'platinum' ? 18 : type === 'gold' ? 16 : 14, fontWeight: 700, color: T.text, marginBottom: type === 'platinum' ? 8 : 6 }}>{sponsor.name}</h3>
                        {sponsor.url && (
                          <a href={sponsor.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: T.primary, textDecoration: "none", display: "inline-block", marginTop: 8 }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"} onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}>
                            Visit Website →
                          </a>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: T.textMuted }}>
              <p>No sponsors available for this event.</p>
              <p className="text-sm mt-2">Check back later for updates.</p>
            </div>
          )}

          {/* ─── CTA – Become a Sponsor ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{
              marginTop: 70,
              background: T.gradNavy,
              borderRadius: 20,
              padding: "48px 56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 32,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 100%, rgba(212,160,23,0.12) 0%, transparent 60%)", pointerEvents: "none" }} />
            
            <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 20, background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.25)", color: T.accentGoldLight, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>
                Interested in Sponsoring?
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.4rem, 2.5vw, 2rem)", fontWeight: 900, color: T.white, letterSpacing: "-0.025em", lineHeight: 1.15 }}>
                Let's <span style={{ color: T.accentGold }}>Partner</span> Together
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted, marginTop: 8, lineHeight: 1.6 }}>
                Contact us today to discuss customized sponsorship packages tailored to your goals.
              </p>
            </div>

            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", position: "relative", zIndex: 1 }}>
              <Link
                to={`/contact${eventId ? `?eventId=${eventId}` : ''}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 28px",
                  background: T.gradGold,
                  color: T.navyDeep,
                  borderRadius: 50,
                  textDecoration: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "all .2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = T.gradGoldReverse;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = T.shadowGold;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = T.gradGold;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Contact Sponsorship Team
                <RiArrowRightLine size={15} />
              </Link>
              <Link
                to="/about"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 28px",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: T.white,
                  borderRadius: 50,
                  textDecoration: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "all .2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Learn About Us
                <RiArrowRightLine size={15} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* ─── BACK TO TOP ─── */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradPrimary,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.white,
              boxShadow: T.shadowMd,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
};

export default Globalsponser;